/**
 * Seeded synthetic datasets for practice: one variable, a correlated pair, or groups
 * with different means. Every dataset is built from a seed text, so the same inputs
 * always give the same table; `exact` makes the sample statistics equal the targets
 * (a textbook-clean example) instead of merely close to them.
 */

import { pearson } from "@/lib/stats/descriptive";
import { momentsOf } from "./clt";
import { correlatedNormals, standardize } from "./multivariate";
import { createNormalSampler, createRng } from "./prng";

export type SingleDistribution =
  | "normal"
  | "uniform"
  | "skewed"
  | "integer"
  | "binary";

export interface NumberColumn {
  kind: "number";
  name: string;
  values: number[];
  /** Decimal places written to the CSV. */
  decimals: number;
  /** The optional row-number column, left out of the summary statistics. */
  isId?: boolean;
}

export interface TextColumn {
  kind: "text";
  name: string;
  values: string[];
}

export type Column = NumberColumn | TextColumn;

export interface Dataset {
  columns: Column[];
  rows: number;
}

/** Draws for a single variable. `p1` and `p2` are the distribution's two parameters. */
export function singleValues(
  distribution: SingleDistribution,
  p1: number,
  p2: number,
  n: number,
  seed: string,
  exact = false
): number[] {
  const rng = createRng(seed);
  const normal = createNormalSampler(rng);
  switch (distribution) {
    case "normal": {
      const draws = Array.from({ length: n }, () => normal());
      return exact
        ? standardize(draws).map((z) => p1 + p2 * z)
        : draws.map((z) => p1 + p2 * z);
    }
    case "uniform":
      return Array.from({ length: n }, () => p1 + (p2 - p1) * rng());
    case "skewed":
      // Exponential waiting above a floor: minimum p1, mean p2.
      return Array.from(
        { length: n },
        () => p1 - (p2 - p1) * Math.log(1 - rng())
      );
    case "integer":
      return Array.from({ length: n }, () =>
        Math.floor(p1 + rng() * (p2 - p1 + 1))
      );
    default:
      return Array.from({ length: n }, () => (rng() < p1 ? 1 : 0));
  }
}

export interface PairSpec {
  meanX: number;
  sdX: number;
  meanY: number;
  sdY: number;
  r: number;
}

/** Two correlated normal columns, by Cholesky. */
export function pairValues(
  spec: PairSpec,
  n: number,
  seed: string,
  exact = false
): [number[], number[]] {
  const normal = createNormalSampler(createRng(seed));
  const [x, y] = correlatedNormals(
    n,
    [spec.meanX, spec.meanY],
    [spec.sdX, spec.sdY],
    [
      [1, spec.r],
      [spec.r, 1],
    ],
    normal,
    exact
  );
  return [x, y];
}

export interface GroupSpec {
  names: string[];
  means: number[];
  sd: number;
  perGroup: number;
}

/** Group labels and a normal outcome per group, groups in blocks. */
export function groupValues(
  spec: GroupSpec,
  seed: string,
  exact = false
): { labels: string[]; values: number[] } {
  const normal = createNormalSampler(createRng(seed));
  const labels: string[] = [];
  const values: number[] = [];
  spec.names.forEach((name, index) => {
    const draws = Array.from({ length: spec.perGroup }, () => normal());
    const z = exact ? standardize(draws) : draws;
    for (const value of z) {
      labels.push(name);
      values.push(spec.means[index] + spec.sd * value);
    }
  });
  return { labels, values };
}

export function idColumn(name: string, n: number): NumberColumn {
  return {
    kind: "number",
    name,
    values: Array.from({ length: n }, (_, i) => i + 1),
    decimals: 0,
    isId: true,
  };
}

const NEEDS_QUOTES = /[",\r\n]/;

/** A CSV field: quoted, with quotes doubled, when it holds a comma, quote or newline. */
export function csvField(text: string): string {
  return NEEDS_QUOTES.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

function cell(column: Column, row: number): string {
  if (column.kind === "text") {
    return csvField(column.values[row]);
  }
  const value = column.values[row];
  const text = value.toFixed(column.decimals);
  // Never write "-0.00" for a value that rounds to zero.
  return Number(text) === 0 ? (0).toFixed(column.decimals) : text;
}

/** The first `limit` rows (all when omitted) as CSV text with a header row. */
export function toCsv(dataset: Dataset, limit = dataset.rows): string {
  const lines = [dataset.columns.map((c) => csvField(c.name)).join(",")];
  for (let row = 0; row < Math.min(limit, dataset.rows); row++) {
    lines.push(dataset.columns.map((c) => cell(c, row)).join(","));
  }
  return lines.join("\n");
}

export interface ColumnSummary {
  name: string;
  count: number;
  mean: number;
  sd: number;
  min: number;
  max: number;
}

export function summarizeColumn(column: NumberColumn): ColumnSummary {
  const moments = momentsOf(column.values, column.values.length);
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const value of column.values) {
    min = Math.min(min, value);
    max = Math.max(max, value);
  }
  return {
    name: column.name,
    count: moments.count,
    mean: moments.mean,
    sd: moments.sd,
    min,
    max,
  };
}

/** Sample mean and SD of the outcome within each group, in group order. */
export function summarizeGroups(
  labels: readonly string[],
  values: readonly number[],
  names: readonly string[]
) {
  return names.map((name) => {
    const inGroup = values.filter((_, i) => labels[i] === name);
    const moments = momentsOf(inGroup, inGroup.length);
    return { name, count: moments.count, mean: moments.mean, sd: moments.sd };
  });
}

export function sampleCorrelation(x: readonly number[], y: readonly number[]) {
  return pearson(x, y);
}

/** The first `limit` rows as formatted text, for an on-screen preview. */
export function previewRows(dataset: Dataset, limit: number): string[][] {
  const rows: string[][] = [];
  for (let row = 0; row < Math.min(limit, dataset.rows); row++) {
    rows.push(dataset.columns.map((c) => cell(c, row)));
  }
  return rows;
}
